import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FaFilter, FaTimes } from 'react-icons/fa';
import styles from './IncidentTimelineFilterMenu.module.css';
import { NOTE_SUB_IDS, NOTE_GROUP_IDS, toggleFilterType, isNoteSubActive } from './timelineFilterUtils';

/**
 * IncidentTimelineFilterMenu
 *
 * Mobile/tablet filter: icon button (with active-count badge) opens a panel
 * of toggle pills. Selections apply immediately — no Apply/Cancel needed.
 * Dismiss via the X button or tap outside. Note and sub-types are mutually
 * exclusive.
 */
function computePanelPos(buttonEl) {
  const rect = buttonEl.getBoundingClientRect();
  const right = window.innerWidth - rect.right;
  const spaceBelow = window.innerHeight - rect.bottom - 8;
  const spaceAbove = rect.top - 8;
  if (spaceBelow < 150 && spaceAbove > spaceBelow) {
    return { bottom: window.innerHeight - rect.top + 8, right, maxHeight: spaceAbove };
  }
  return { top: rect.bottom + 8, right, maxHeight: Math.max(120, spaceBelow) };
}

const IncidentTimelineFilterMenu = ({ availableTypes, appliedTypes, onApply }) => {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, right: 0, maxHeight: undefined });

  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  useLayoutEffect(() => {
    if (open && buttonRef.current) {
      setPanelPos(computePanelPos(buttonRef.current));
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const updatePos = () => {
      if (buttonRef.current) setPanelPos(computePanelPos(buttonRef.current));
    };
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [open]);

  useEffect(() => {
    if (open && panelRef.current) panelRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const closePanel = useCallback(() => {
    setOpen(false);
    buttonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => { if (e.key === 'Escape') closePanel(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, closePanel]);

  function toggle(id) {
    onApply(toggleFilterType(appliedTypes, id));
  }

  const handleClearAll = useCallback(() => onApply(new Set()), [onApply]);

  const activeCount = appliedTypes.size;
  const isActive = activeCount > 0;

  const topTypes = availableTypes.filter(({ id }) => !NOTE_GROUP_IDS.has(id));
  const noteGroupTypes = availableTypes.filter(({ id }) => NOTE_GROUP_IDS.has(id));
  const noteSubActive = isNoteSubActive(appliedTypes);

  function renderPill({ id, label }, extraClass) {
    const pillActive = appliedTypes.has(id) || (id === 'note' && noteSubActive);
    return (
      <button
        key={id}
        type="button"
        data-type={id}
        className={[styles.pill, extraClass, pillActive ? styles.pillActive : ''].filter(Boolean).join(' ')}
        onClick={() => toggle(id)}
        aria-pressed={pillActive}
      >
        {label}
      </button>
    );
  }

  return (
    <div className={styles.menuContainer}>
      <button
        ref={buttonRef}
        type="button"
        className={`${styles.filterButton} ${isActive ? styles.filterButtonActive : ''}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={isActive ? `Filter (${activeCount} active)` : 'Filter'}
      >
        <FaFilter aria-hidden="true" />
        {isActive && (
          <span className={styles.filterCountBadge} aria-hidden="true">
            {activeCount}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          className={styles.panel}
          role="dialog"
          aria-label="Filter timeline"
          tabIndex={-1}
          style={{
            right: panelPos.right,
            maxHeight: panelPos.maxHeight,
            ...(panelPos.top !== undefined ? { top: panelPos.top } : { bottom: panelPos.bottom }),
          }}
        >
          <div className={styles.panelHeader}>
            <span className={styles.panelTitle}>Filter by</span>
            <button
              type="button"
              className={styles.closeButton}
              onClick={closePanel}
              aria-label="Close filter panel"
            >
              <FaTimes aria-hidden="true" />
            </button>
          </div>

          <div className={styles.pillList}>
            {topTypes.map((t) => renderPill(t, null))}
            {noteGroupTypes.length > 0 && (
              <div className={styles.noteGroup} role="group" aria-label="Note filters">
                {noteGroupTypes.map((t) =>
                  renderPill(t, NOTE_SUB_IDS.has(t.id) ? styles.pillSub : null)
                )}
              </div>
            )}
          </div>

          {isActive && (
            <div className={styles.panelFooter}>
              <button type="button" className={styles.clearAllButton} onClick={handleClearAll}>
                Clear all filters
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

IncidentTimelineFilterMenu.propTypes = {
  availableTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  appliedTypes: PropTypes.instanceOf(Set).isRequired,
  onApply: PropTypes.func.isRequired,
};

export default IncidentTimelineFilterMenu;
